import { useEffect, useState, type FormEvent } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEye,
  faEyeSlash,
  faSpinner,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { Modal } from '@/components/shared/Modal';
import {
  PLATFORM_ROLES,
  type CreatePlatformUserRequest,
  type PlatformRole,
} from '@/types/platform-user.types';
import { roleLabel } from './user-presentation';

/**
 * Phase 12.6-5 (completion) — Create User.
 *
 * Design of record: `PLATFORM_USERS_DESIGN.md` v1.0 §6 — *"Modal: email,
 * username (optional), display name, role, password (≥ 12 chars) →
 * `POST /platform/users`"*, and §8.1's direct-creation flow (OD-B).
 *
 * The 12.6-6 readiness audit found this to be the one item on the design's
 * 12.6-5 row that was never built: the backend route, the request type and
 * `platformUserService.create()` all existed, but no component called them —
 * `create` was the sole orphan of the seven service methods. This closes that.
 *
 * ── Exactly five fields ─────────────────────────────────────────────────────
 * `email`, `username?`, `displayName`, `role`, `password`. Nothing else. There
 * is no `isActive` (the column defaults to true and the server sets it), no
 * `tokenVersion`, no `tenantId` — and **no password-confirmation field**,
 * because §6 specifies five fields and adding a sixth would put the UI ahead of
 * the approved design.
 *
 * ── No confirmation step ────────────────────────────────────────────────────
 * §6: *"No — the modal is the deliberation."* Creating an account is not
 * destructive and is fully reversible by deactivation, so a second confirm would
 * be ceremony where nothing is at stake.
 *
 * ── The password never leaves this component ────────────────────────────────
 * It is typed here, sent once, and cleared. It is never logged, never put in a
 * toast, never echoed back, and never retained after a successful creation. The
 * server never returns it — the response is the eight-field allow-list.
 *
 * ── Typed state survives a rejected submit ──────────────────────────────────
 * `submitting` and `error` are PROPS, owned by the page, so the dialog stays
 * mounted through a failure with every field intact. **The password is
 * preserved too**, deliberately: a 409 on a duplicate *email* must not cost the
 * operator a retyped twelve-character password on an unrelated field. Nothing in
 * this repository establishes a convention of clearing it on error for an
 * administrative create form — the one `setPassword('')` elsewhere is a customer
 * activation flow, a different situation. It IS cleared on success, on cancel,
 * and on every fresh open.
 */

interface Props {
  open: boolean;
  submitting: boolean;
  error: string;
  onClose: () => void;
  onSubmit: (payload: CreatePlatformUserRequest) => void;
}

/** Mirrors the server's `@MinLength(12)` — `SECURITY_RULES.md`. */
const PASSWORD_MIN_LENGTH = 12;
/** Mirrors the server's `@MaxLength(200)`. */
const PASSWORD_MAX_LENGTH = 200;

export function PlatformUserCreateDialog({ open, submitting, error, onClose, onSubmit }: Props) {
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<PlatformRole | ''>('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Every fresh open starts empty — values from one creation attempt must never
  // appear in a later one. This runs on open only, so it never fights typing.
  useEffect(() => {
    if (!open) return;
    setEmail('');
    setUsername('');
    setDisplayName('');
    setRole('');
    setPassword('');
    setShowPassword(false);
  }, [open]);

  if (!open) return null;

  // Normalised the same way the server normalises, so what is validated here is
  // what is stored there.
  const nextEmail = email.trim().toLowerCase();
  const nextDisplayName = displayName.trim();
  const nextUsername = username.trim();

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(nextEmail) && nextEmail.length <= 255;
  const displayNameValid = nextDisplayName.length > 0 && nextDisplayName.length <= 255;
  const usernameValid = nextUsername.length <= 100;
  const roleValid = role !== '';
  const passwordValid =
    password.length >= PASSWORD_MIN_LENGTH && password.length <= PASSWORD_MAX_LENGTH;

  const canSubmit =
    !submitting && emailValid && displayNameValid && usernameValid && roleValid && passwordValid;

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    if (!canSubmit) return;

    const payload: CreatePlatformUserRequest = {
      email: nextEmail,
      displayName: nextDisplayName,
      role: role as PlatformRole,
      password,
    };
    // Omitted rather than sent empty — the type marks it optional, and an empty
    // string is not the same as "no username".
    if (nextUsername !== '') payload.username = nextUsername;

    onSubmit(payload);
  };

  return (
    <Modal
      open
      onClose={submitting ? () => undefined : onClose}
      title="New platform user"
      size="md"
    >
      <form onSubmit={submit} className="space-y-4 px-6 py-5">
        <p className="text-sm text-muted-foreground">
          The account is active immediately. Give the password to the person directly — it is shown
          only while you type it here and is never displayed again.
        </p>

        <div>
          <label
            htmlFor="create-user-email"
            className="mb-1.5 block text-sm font-medium text-foreground"
          >
            Email <span className="text-destructive">*</span>
          </label>
          <input
            id="create-user-email"
            type="email"
            value={email}
            maxLength={255}
            autoComplete="off"
            onChange={(e) => setEmail(e.target.value)}
            placeholder="ops@service-portal.local"
            className="h-11 w-full rounded-lg border border-border bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring sm:h-10"
          />
          <p className="mt-1.5 text-xs text-muted-foreground">
            This is their sign-in identifier. Stored lowercase.
          </p>
        </div>

        <div>
          <label
            htmlFor="create-user-display-name"
            className="mb-1.5 block text-sm font-medium text-foreground"
          >
            Display name <span className="text-destructive">*</span>
          </label>
          <input
            id="create-user-display-name"
            type="text"
            value={displayName}
            maxLength={255}
            autoComplete="off"
            onChange={(e) => setDisplayName(e.target.value)}
            className="h-11 w-full rounded-lg border border-border bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring sm:h-10"
          />
        </div>

        <div>
          <label
            htmlFor="create-user-username"
            className="mb-1.5 block text-sm font-medium text-foreground"
          >
            Username <span className="text-muted-foreground">(optional)</span>
          </label>
          <input
            id="create-user-username"
            type="text"
            value={username}
            maxLength={100}
            autoComplete="off"
            onChange={(e) => setUsername(e.target.value)}
            className="h-11 w-full rounded-lg border border-border bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring sm:h-10"
          />
          <p className="mt-1.5 text-xs text-muted-foreground">
            Not a sign-in identifier on the platform — a label only.
          </p>
        </div>

        <div>
          <label
            htmlFor="create-user-role"
            className="mb-1.5 block text-sm font-medium text-foreground"
          >
            Role <span className="text-destructive">*</span>
          </label>
          <select
            id="create-user-role"
            value={role}
            onChange={(e) => setRole(e.target.value as PlatformRole)}
            className="h-11 w-full rounded-lg border border-border bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring sm:h-10"
          >
            <option value="">Select a role…</option>
            {/* The seven frozen D-041 roles, from the single authoritative
                source. Never a hand-written list. */}
            {PLATFORM_ROLES.map((r) => (
              <option key={r} value={r}>
                {roleLabel(r)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            htmlFor="create-user-password"
            className="mb-1.5 block text-sm font-medium text-foreground"
          >
            Password <span className="text-destructive">*</span>
          </label>
          <div className="relative">
            <input
              id="create-user-password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              maxLength={PASSWORD_MAX_LENGTH}
              autoComplete="new-password"
              onChange={(e) => setPassword(e.target.value)}
              className="h-11 w-full rounded-lg border border-border bg-background px-3 pr-11 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring sm:h-10"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              className="absolute right-0 top-0 flex h-11 w-11 items-center justify-center text-muted-foreground transition-colors hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring sm:h-10 sm:w-10"
            >
              <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} className="h-3.5 w-3.5" />
            </button>
          </div>
          <p className="mt-1.5 text-xs text-muted-foreground">
            At least {PASSWORD_MIN_LENGTH} characters.
            {password.length > 0 && password.length < PASSWORD_MIN_LENGTH && (
              // Counts characters — never echoes the value.
              <span className="text-destructive">
                {' '}
                {PASSWORD_MIN_LENGTH - password.length} more needed.
              </span>
            )}
          </p>
        </div>

        {error && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2"
          >
            <FontAwesomeIcon
              icon={faTriangleExclamation}
              className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive"
            />
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {/* Full-width, stacked below sm — Modal Standards. */}
        <div className="flex flex-col-reverse gap-2 pt-1 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="min-h-11 rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:opacity-50 sm:min-h-0"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className="min-h-11 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
          >
            {submitting && <FontAwesomeIcon icon={faSpinner} spin className="mr-2 h-3.5 w-3.5" />}
            Create user
          </button>
        </div>
      </form>
    </Modal>
  );
}
